import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import type { OrgRecord, Member, Activity } from '../services/api';
import './OrgPage.css';

const ACTION_LABELS: Record<string, string> = {
  org_created: 'Created the organisation',
  member_joined: 'Joined the organisation',
  file_uploaded: 'Uploaded a file',
  file_deleted: 'Deleted a file',
  file_shared: 'Shared a file with the org',
  query_asked: 'Ran a query',
};

const ACTION_ICONS: Record<string, string> = {
  org_created: '🏢',
  member_joined: '👤',
  file_uploaded: '📄',
  file_deleted: '🗑',
  file_shared: '🔗',
  query_asked: '💬',
};

function timeAgo(ts: number): string {
  // Handle both seconds and milliseconds timestamps
  const tsSeconds = ts > 9999999999 ? Math.floor(ts / 1000) : ts;
  const diff = Math.floor(Date.now() / 1000) - tsSeconds;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(tsSeconds * 1000).toLocaleDateString();
}

export default function OrgPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState<OrgRecord[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<OrgRecord | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [orgFiles, setOrgFiles] = useState<any[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [activeTab, setActiveTab] = useState<'members' | 'files' | 'activity'>('members');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'member' | 'admin'>('member');
  const [inviteToken, setInviteToken] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadOrgs = useCallback(async () => {
    try {
      const res = await api.get('/orgs');
      setOrgs(res.data.orgs || []);
    } catch { setError('Failed to load organisations'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { loadOrgs(); }, [loadOrgs]);

  const loadMembers = useCallback(async (orgId: string) => {
    try {
      const res = await api.get(`/orgs/${orgId}/members`);
      setMembers(res.data.members || []);
    } catch { setMembers([]); }
  }, [api]);

  useEffect(() => {
    if (!selectedOrg) return;
    loadMembers(selectedOrg.org_id);
    api.get(`/orgs/${selectedOrg.org_id}/files`).then(r => setOrgFiles(r.data.files || [])).catch(() => setOrgFiles([]));
    api.get(`/orgs/${selectedOrg.org_id}/activity`).then(r => setActivities(r.data.activities || [])).catch(() => setActivities([]));
  }, [selectedOrg, loadMembers, api]);

  const createOrg = async () => {
    if (!newOrgName.trim()) return;
    try {
      const res = await api.post('/orgs', { name: newOrgName.trim() });
      setOrgs(prev => [res.data, ...prev]);
      setNewOrgName('');
      setCreating(false);
      setSuccess('Organisation created');
    } catch (e: any) { setError(e.response?.data?.detail || 'Failed to create org'); }
  };

  const inviteMember = async () => {
    if (!selectedOrg || !inviteEmail.trim()) return;
    try {
      const res = await api.post(`/orgs/${selectedOrg.org_id}/invites`, { email: inviteEmail.trim(), role: inviteRole });
      setInviteToken(res.data.invite_token);
      setInviteEmail('');
      setSuccess('Invite created');
    } catch (e: any) { setError(e.response?.data?.detail || 'Failed to invite'); }
  };

  const removeMember = async (userId: string) => {
    if (!selectedOrg || !window.confirm('Remove this member?')) return;
    try {
      await api.delete(`/orgs/${selectedOrg.org_id}/members/${userId}`);
      setMembers(prev => prev.filter(m => m.user_id !== userId));
      setSuccess('Member removed');
    } catch (e: any) { setError(e.response?.data?.detail || 'Failed to remove'); }
  };

  return (
    <div className="org-page">
      <div className="org-sidebar">
        <div className="org-sidebar-header">
          <h2 className="org-sidebar-title">Organisations</h2>
          <button className="btn btn-primary org-create-btn" onClick={() => setCreating(c => !c)}>+ New</button>
        </div>

        {creating && (
          <div className="org-create-form card fade-in">
            <input value={newOrgName} onChange={e => setNewOrgName(e.target.value)}
              placeholder="Organisation name..." onKeyDown={e => e.key === 'Enter' && createOrg()} autoFocus />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={createOrg} style={{ flex: 1 }}>Create</button>
              <button className="btn btn-ghost" onClick={() => setCreating(false)}>Cancel</button>
            </div>
          </div>
        )}

        {loading ? <div className="org-loading"><span className="spinner" /></div> : (
          <div className="org-list">
            {orgs.length === 0 && <p className="org-empty">No organisations yet</p>}
            {orgs.map(o => (
              <div key={o.org_id} className={`org-list-item ${selectedOrg?.org_id === o.org_id ? 'active' : ''}`}
                onClick={() => setSelectedOrg(o)}>
                <span className="org-list-icon">🏢</span>
                <div><p className="org-list-name">{o.name}</p></div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="org-main">
        {error && <div className="org-msg error">{error} <button onClick={() => setError('')}>✕</button></div>}
        {success && <div className="org-msg success">{success} <button onClick={() => setSuccess('')}>✕</button></div>}

        {!selectedOrg ? (
          <div className="org-main-empty">
            <span style={{ fontSize: 40 }}>🏢</span>
            <p>Select an organisation or create a new one</p>
          </div>
        ) : (
          <div className="org-detail fade-in">
            <h2 className="org-detail-title">{selectedOrg.name}</h2>

            <div className="org-stats">
              <div className="org-stat"><span className="org-stat-value">{members.length}</span><span className="org-stat-label">Members</span></div>
              <div className="org-stat"><span className="org-stat-value">{orgFiles.length}</span><span className="org-stat-label">Files</span></div>
              <div className="org-stat"><span className="org-stat-value">{activities.length}</span><span className="org-stat-label">Activities</span></div>
            </div>

            <div className="org-tabs">
              <button className={`org-tab ${activeTab === 'members' ? 'active' : ''}`} onClick={() => setActiveTab('members')}>Members</button>
              <button className={`org-tab ${activeTab === 'files' ? 'active' : ''}`} onClick={() => setActiveTab('files')}>Files</button>
              <button className={`org-tab ${activeTab === 'activity' ? 'active' : ''}`} onClick={() => setActiveTab('activity')}>Activity</button>
            </div>

            {activeTab === 'members' && (
              <>
                <div className="card org-invite-section">
                  <h3 className="org-section-title">Invite Member</h3>
                  <div className="org-invite-row">
                    <input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                      placeholder="Email address..." style={{ flex: 1 }} />
                    <select value={inviteRole} onChange={e => setInviteRole(e.target.value as any)}>
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                    </select>
                    <button className="btn btn-primary" onClick={inviteMember}>Send Invite</button>
                  </div>
                  {inviteToken && (
                    <div className="org-invite-token fade-in">
                      <p className="org-token-label">Share this invite token:</p>
                      <code className="org-token">{inviteToken}</code>
                      <button className="btn btn-ghost" style={{ fontSize: 12 }}
                        onClick={() => { navigator.clipboard.writeText(inviteToken); setSuccess('Copied!'); }}>Copy</button>
                    </div>
                  )}
                </div>

                <div className="card">
                  <h3 className="org-section-title">Members ({members.length})</h3>
                  <div className="org-members-list">
                    {members.map(m => (
                      <div key={m.user_id} className="org-member-row">
                        <div className="org-member-avatar">{(m.name || m.email || '?')[0].toUpperCase()}</div>
                        <div className="org-member-info">
                          <p className="org-member-name">{m.name || m.email || 'Unknown user'}</p>
                          {m.email && m.email !== m.name && <p className="org-member-email">{m.email}</p>}
                          {!m.name && !m.email && <p className="org-member-email">{m.user_id.slice(0, 12)}...</p>}
                        </div>
                        <span className={`badge ${m.role === 'admin' ? 'badge-accent' : 'badge-green'}`}>{m.role}</span>
                        <button className="btn btn-ghost org-remove-btn" onClick={() => removeMember(m.user_id)} title="Remove">✕</button>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {activeTab === 'files' && (
              <div className="card">
                <h3 className="org-section-title">Organisation Files ({orgFiles.length})</h3>
                {orgFiles.length === 0 ? (
                  <p className="org-empty">No files uploaded by org members yet</p>
                ) : (
                  <div className="org-files-list">
                    {orgFiles.map((f: any) => (
                      <div key={f.file_id || f.table_id} className="org-file-row" onClick={() => navigate(`/workspace/${f.file_id}`)}>
                        <span className="org-file-icon">📄</span>
                        <div className="org-file-info">
                          <span className="org-file-name">{f.file_name}</span>
                          <span className="org-file-meta">
                            by {f.uploaded_by || 'Unknown'} · {new Date((f.created_at || 0) * 1000).toLocaleDateString()}
                            {f.is_own && <span className="badge badge-green" style={{ marginLeft: 8 }}>yours</span>}
                          </span>
                        </div>
                        <span className="badge badge-accent">{f.total_tables || 0} tables</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="card">
                <h3 className="org-section-title">Recent Activity</h3>
                {activities.length === 0 ? (
                  <p className="org-empty">No activity yet</p>
                ) : (
                  <div className="org-activity-list">
                    {activities.map(a => (
                      <div key={a.activity_id} className="org-activity-row">
                        <span className="org-activity-icon">{ACTION_ICONS[a.action] || '📌'}</span>
                        <div className="org-activity-info">
                          <p className="org-activity-text">
                            <span className="org-activity-user">{a.user_name}</span>
                            {' '}{ACTION_LABELS[a.action] || a.action}
                            {a.details && <span className="org-activity-detail"> — {a.details}</span>}
                          </p>
                          <p className="org-activity-time">{timeAgo(a.created_at)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
